/**
 * MobileWorkspaceSwitcher — the frosted workspace picker, opened by tapping
 * the two-line "WORKSPACE / <name>" brand label while inside a workspace.
 * Same visual kit as the launcher's account sheet: deep fog backdrop
 * (§10.5 transient-popover glass), a full-screen `safe center`ed sheet of
 * .ws-home-tile cards.
 *
 * ACTIVE WORKSPACES = every OPEN workspace (server open-list, the same
 * sidebar-tabs state desktop's WorkspaceManager tracks) — the current one
 * carries the shine border; each card has a × that closes it (markClosed,
 * desktop parity). RECENT = recently opened workspaces that are currently
 * closed. Tap a card to switch in place; fog / empty space / Esc dismisses.
 *
 * Portals to document.body (outside any ThemedArea), so it applies the
 * theme class + ws-clear explicitly — same rules as MobileNavOverlay.
 */
import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { LayoutGrid, List, X } from 'lucide-react'
import { useTheme, getThemeClass } from '@papan/core-ui/ui'
import { workspaceApi, getWorkspaceName } from '@papan/workspace/client'
import { useTenant } from '../hooks/useTenant'

interface OpenWs {
  id: string
  folderId: string
  name: string
  path?: string
}

interface MobileWorkspaceSwitcherProps {
  /** Folder id of the workspace currently open (the URL param). */
  activeFolderId: string
  onClose: () => void
}

export function MobileWorkspaceSwitcher({ activeFolderId, onClose }: MobileWorkspaceSwitcherProps) {
  const navigate = useNavigate()
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const { tenantId, isPersonal, activeOrgEmail } = useTenant()
  const queryClient = useQueryClient()
  const prefix = isPersonal ? '/~' : `/t/${tenantId}`

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  // Open list is the source of truth for the ACTIVE section; recents fill
  // the RECENT section minus whatever is open. Keys shared with the rest
  // of the mobile shell so caches coalesce.
  const openKey = ['mobile-open-workspaces', tenantId, activeOrgEmail ?? '']
  const openQuery = useQuery({
    queryKey: openKey,
    queryFn: () => workspaceApi.listOpen(),
    staleTime: 15_000,
  })
  const recentQuery = useQuery({
    queryKey: ['mobile-recent-workspaces', tenantId, activeOrgEmail ?? ''],
    queryFn: () => workspaceApi.listRecent(),
    staleTime: 30_000,
  })

  const open: OpenWs[] = openQuery.data ?? []
  const openIds = new Set(open.map((w) => w.folderId))
  const recents = (recentQuery.data ?? []).filter((w) => !openIds.has(w.folderId))

  const pick = (folderId: string) => {
    if (folderId !== activeFolderId) navigate(`${prefix}/workspace/${folderId}`)
    onClose()
  }

  /** Close an OPEN workspace (desktop markClosed parity). Closing the one
   *  you're standing in moves you to the next open workspace, or the list
   *  when it was the last. */
  const closeWs = (w: OpenWs) => {
    workspaceApi.markClosed(w.id).catch(() => {})
    queryClient.setQueryData<OpenWs[]>(openKey, (prev) => (prev ?? []).filter((x) => x.folderId !== w.folderId))
    // Freshly closed → shows under RECENT on next open of the sheet.
    void queryClient.invalidateQueries({ queryKey: ['mobile-recent-workspaces'] })
    if (w.folderId === activeFolderId) {
      const remaining = open.filter((x) => x.folderId !== w.folderId)
      if (remaining.length > 0) {
        navigate(`${prefix}/workspace/${remaining[0].folderId}`)
      } else {
        navigate(`${prefix}/workspace`)
        onClose()
      }
    }
  }

  /** Card row — a div (not button) so the × can be a real button inside. */
  const card = (w: { folderId: string; name: string; path?: string }, opts: { active?: boolean; onCloseCard?: () => void }) => (
    <div
      key={w.folderId}
      role="button"
      tabIndex={0}
      onClick={() => pick(w.folderId)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') pick(w.folderId) }}
      aria-current={opts.active ? 'true' : undefined}
      className={`ws-home-tile mobile-account-card relative shrink-0 cursor-pointer ${opts.active ? 'is-selected' : ''}`}
    >
      {opts.active && <span className="mobile-shine-border" aria-hidden />}
      <span className="ws-project-glyph shrink-0">
        <LayoutGrid size={16} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm themed-text">{getWorkspaceName(w.name)}</span>
        {w.path && <span className="block truncate text-xs themed-text-mid">{w.path}</span>}
      </span>
      {opts.onCloseCard && (
        <button
          onClick={(e) => { e.stopPropagation(); opts.onCloseCard!() }}
          aria-label={`Close ${getWorkspaceName(w.name)}`}
          className="relative z-10 -mr-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg themed-text-mid active:themed-bg-tertiary"
        >
          <X size={15} />
        </button>
      )}
    </div>
  )

  return createPortal(
    // ws-clear is LOAD-BEARING (see MobileNavOverlay): without it the theme
    // class paints an opaque --bg-primary and the fog has nothing to frost.
    <div className={`${themeClass} ws-clear fixed inset-0 z-[200]`}>
      <div className="mobile-fog mobile-fog-deep mobile-fog-in absolute inset-0" onClick={onClose} aria-hidden />
      <div
        onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
        className="mobile-sheet-in mobile-account-sheet absolute inset-0 flex w-full flex-col overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <p className="mobile-switcher-label px-2 text-[10px] uppercase tracking-wide themed-text-mid">Active workspaces</p>
        {open.map((w) => card(w, { active: w.folderId === activeFolderId, onCloseCard: () => closeWs(w) }))}
        {open.length === 0 && !openQuery.isLoading && (
          <p className="px-2 text-xs themed-text-muted">No open workspaces</p>
        )}

        {recents.length > 0 && (
          <p className="mobile-switcher-label px-2 pt-2 text-[10px] uppercase tracking-wide themed-text-mid">Recent</p>
        )}
        {recents.map((w) => card(w, {}))}

        {/* Escape hatch to the full list — a DARK, half-transparent panel
            (frosts the fog through it), but with the SAME glass border + shadow
            as the workspace cards (ws-home-tile vars), not a black border. */}
        <button
          onClick={() => { navigate(`${prefix}/workspace`); onClose() }}
          style={{ background: 'rgba(0, 0, 0, 0.4)', border: '1px solid var(--ws-dpill-border)', boxShadow: 'var(--ws-dpill-shadow)' }}
          className="mobile-account-card flex shrink-0 items-center gap-2.5 px-3.5 py-2.5 text-left active:themed-bg-tertiary"
        >
          <span className="ws-project-glyph shrink-0">
            <List size={15} />
          </span>
          <span className="text-sm themed-text">All workspaces</span>
        </button>
      </div>
    </div>,
    document.body,
  )
}
